// ============================================
// BulkUpdateCategoryModal
// Gán một danh mục cho nhiều sản phẩm cùng lúc
// ============================================
//
// Endpoint: `POST /products/bulk-update/` — hợp đồng chốt với BE 19/09/2026,
// xem `docs/BE_REQUEST_bulk_update_products.md`.
//
// Nhánh 404 vẫn giữ: nếu môi trường đang chạy bản BE cũ hơn, modal hiện câu
// "tính năng chưa lên" thay vì message lỗi kỹ thuật. Cùng cách
// `InventoryLossStatsPage` xử lý `/inventory/loss-stats/` lúc chờ deploy.
//
// PHẠM VI CỐ Ý HẸP: chỉ đổi DANH MỤC, không phải "sửa field bất kỳ hàng loạt".
// Mỗi field sản phẩm có ràng buộc riêng — giá phải chuẩn hoá, SKU phải là duy
// nhất, nhóm 18+ ràng buộc lẫn nhau — nên một form vạn năng sẽ phải nhân bản
// toàn bộ validate của form đơn lẻ, và sai ở quy mô 100 sản phẩm thì không lùi
// được. Danh mục là field an toàn nhất để mở trước: một khoá ngoại, không đụng
// tới tiền hay tồn kho.

import { useState } from 'react';
import type { BulkUpdateProductsResponse } from '@conai/shared-types';
import type { SelectOption } from '../../components/shared';
import { useI18n } from '../../i18n';
import { useBulkUpdateProducts } from '../../hooks';
import { SearchableSelect } from './SearchableSelect';

/** Trần mỗi lượt — cùng con số với `POST /products/bulk/`. */
const MAX_IDS = 100;

interface BulkUpdateCategoryModalProps {
  /** Id sản phẩm đang chọn. */
  productIds: string[];
  categoryOptions: SelectOption[];
  onClose: () => void;
  /** Gọi khi lưu xong — nơi gọi dùng để xoá lựa chọn. */
  onDone?: () => void;
}

/** Giá trị đại diện cho "gỡ danh mục" trong droplist. */
const CLEAR_VALUE = '__clear__';

/**
 * Bảng kết quả sau khi chạy — chỉ hiện khi có gì đó người dùng cần biết.
 *
 * Ba con số, mỗi con số trả lời một câu khác nhau:
 *   đã đổi    — việc thật sự xảy ra
 *   vốn đã đúng — giải thích vì sao Audit Logs không có dòng nào cho chúng
 *   hỏng      — kèm lý do từng cái, vì đó là thứ người dùng phải đi xử lý tiếp
 *
 * `unchanged` KHÔNG cộng vào `updated`: nó là tập con. Hiển thị tách ra cho dễ
 * đọc, nhưng phép tính phải trừ, không phải cộng.
 */
function BulkResultPanel({
  result,
  t,
}: {
  result: BulkUpdateProductsResponse;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const updated = result.summary?.updated ?? result.updated?.length ?? 0;
  const unchanged = result.summary?.unchanged ?? result.unchanged?.length ?? 0;
  const failed = result.summary?.failed ?? result.failed?.length ?? 0;
  const changed = Math.max(updated - unchanged, 0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-emerald-50 px-2 py-2">
          <p className="text-lg font-bold text-emerald-700">{changed}</p>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-600">
            {t('bulkResultChanged')}
          </p>
        </div>
        <div className="rounded-lg bg-slate-100 px-2 py-2">
          <p className="text-lg font-bold text-slate-600">{unchanged}</p>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            {t('bulkResultUnchanged')}
          </p>
        </div>
        <div className={`rounded-lg px-2 py-2 ${failed > 0 ? 'bg-red-50' : 'bg-slate-100'}`}>
          <p className={`text-lg font-bold ${failed > 0 ? 'text-red-700' : 'text-slate-600'}`}>
            {failed}
          </p>
          <p
            className={`text-[11px] font-semibold uppercase tracking-wider ${
              failed > 0 ? 'text-red-600' : 'text-slate-500'
            }`}
          >
            {t('bulkResultFailed')}
          </p>
        </div>
      </div>

      {unchanged > 0 && (
        <p className="text-xs text-slate-500">{t('bulkResultUnchangedHint', { count: unchanged })}</p>
      )}

      {/* Lý do từng dòng hỏng — BE cam kết là câu đọc được, nên hiện thẳng.
          Gộp thành "N sản phẩm lỗi" sẽ buộc người dùng mở Audit Logs đi dò. */}
      {result.failed?.length > 0 && (
        <div className="max-h-40 overflow-y-auto overscroll-contain rounded-lg border border-red-100 bg-red-50 p-2">
          <ul className="space-y-1">
            {result.failed.map((row) => (
              <li key={row.product_id} className="text-xs text-red-700">
                <span className="font-mono text-[10px] text-red-500">
                  {row.product_id.slice(0, 8)}…
                </span>{' '}
                {row.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Mã lô: dán vào ô tìm kiếm Audit Logs để thấy TẤT CẢ sản phẩm đã đổi
          trong cùng một cú bấm. Dòng audit per-product không trả lời được câu
          đó, mà sau một lần bấm nhầm thì đó là câu duy nhất người ta hỏi. */}
      {result.bulk_operation_id && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            {t('bulkResultOperationId')}
          </p>
          <p className="mt-0.5 select-all break-all font-mono text-xs text-slate-700">
            {result.bulk_operation_id}
          </p>
          <p className="mt-1 text-[11px] text-slate-400">{t('bulkResultOperationIdHint')}</p>
        </div>
      )}
    </div>
  );
}

export function BulkUpdateCategoryModal({
  productIds,
  categoryOptions,
  onClose,
  onDone,
}: BulkUpdateCategoryModalProps) {
  const { t } = useI18n();
  const { mutate: bulkUpdate, isPending } = useBulkUpdateProducts();

  const [categoryId, setCategoryId] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** `true` khi BE trả 404 — tính năng chưa deploy, không phải lỗi thao tác. */
  const [notDeployed, setNotDeployed] = useState(false);
  /**
   * Kết quả lượt vừa chạy, GIỮ LẠI trên màn khi có gì đó cần người dùng biết.
   *
   * Đóng modal ngay sau khi gửi là đúng khi mọi thứ sạch. Nhưng nếu có dòng
   * hỏng, hoặc có sản phẩm vốn đã đúng danh mục (BE không ghi audit cho chúng),
   * thì đóng luôn nghĩa là nuốt mất thông tin duy nhất giải thích vì sao kết
   * quả không như người dùng đoán.
   */
  const [result, setResult] = useState<BulkUpdateProductsResponse | null>(null);

  const tooMany = productIds.length > MAX_IDS;

  const handleSubmit = () => {
    setError(null);

    // Chưa chọn gì thì không gửi. KHÔNG mặc định thành "gỡ danh mục" — gỡ là
    // một thao tác phá huỷ, phải do người dùng chọn tường minh.
    if (!categoryId) {
      setError(t('bulkCategoryErrorNoCategory'));
      return;
    }
    if (tooMany) {
      setError(t('bulkCategoryErrorTooMany', { max: MAX_IDS }));
      return;
    }

    bulkUpdate(
      {
        product_ids: productIds,
        category: categoryId === CLEAR_VALUE ? null : categoryId,
      },
      {
        onSuccess: (res) => {
          // Lựa chọn đã xử lý xong dù kết quả ra sao — xoá để người dùng không
          // vô tình bấm lại lên cùng tập sản phẩm.
          onDone?.();

          const failed = res?.summary?.failed ?? res?.failed?.length ?? 0;
          const unchanged = res?.summary?.unchanged ?? res?.unchanged?.length ?? 0;
          if (failed > 0 || unchanged > 0) {
            setResult(res);
            return;
          }
          onClose();
        },
        onError: (err: unknown) => {
          // 404 = endpoint chưa tồn tại. Phân biệt hẳn với lỗi thao tác: hiện
          // message kỹ thuật ở đây sẽ bị đọc thành "mình làm sai gì đó".
          const status = (err as { status?: number })?.status;
          if (status === 404) {
            setNotDeployed(true);
            return;
          }
          setError(
            (err as { message?: string })?.message ?? t('bulkCategoryErrorGeneric')
          );
        },
      }
    );
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-[480px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t('bulkCategoryTitle')}</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {t('bulkCategorySubtitle', { count: productIds.length })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          {notDeployed ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
              <p className="font-semibold">{t('bulkCategoryNotDeployed')}</p>
              <p className="mt-1 text-xs">{t('bulkCategoryNotDeployedHint')}</p>
            </div>
          ) : result ? (
            <BulkResultPanel result={result} t={t} />
          ) : (
            <>
              {error && (
                <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
              )}
              {tooMany && (
                <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  {t('bulkCategoryErrorTooMany', { max: MAX_IDS })}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  {t('categories')} <span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  options={[
                    ...categoryOptions,
                    // Gỡ danh mục là thao tác có thật (sản phẩm xếp nhầm vào
                    // một danh mục sắp xoá), nên phải chọn được — nhưng đứng
                    // CUỐI danh sách, không phải lựa chọn mặc định.
                    { value: CLEAR_VALUE, label: t('bulkCategoryClearOption') },
                  ]}
                  value={categoryId}
                  onChange={setCategoryId}
                  placeholder={t('bulkCategorySelectPlaceholder')}
                />
              </div>

              {/* Nói trước hậu quả: đây là thao tác GHI ĐÈ, danh mục cũ của
                  từng sản phẩm mất luôn và không có nút hoàn tác. */}
              <p className="text-xs text-slate-500">
                {categoryId === CLEAR_VALUE
                  ? t('bulkCategoryWarnClear', { count: productIds.length })
                  : t('bulkCategoryWarnOverwrite', { count: productIds.length })}
              </p>

              {/* Phạm vi ảnh hưởng, BE nêu ra 19/09/2026: `Product` KHÔNG có
                  khoá ngoại tới cửa hàng — catalogue dùng chung toàn chuỗi. Nên
                  một STORE_ADMIN của một cửa hàng đổi được danh mục của sản
                  phẩm mọi cửa hàng đang bán.
                  Đúng như `PATCH /products/{id}/` vẫn luôn thế, nhưng ở quy mô
                  100 sản phẩm một cú bấm thì phải nói ra, không để người ta tự
                  phát hiện sau khi đã bấm. */}
              <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {t('bulkCategoryChainWideWarning')}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button
            onClick={onClose}
            disabled={isPending}
            className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 disabled:opacity-50"
          >
            {notDeployed || result ? t('close') : t('cancel')}
          </button>
          {!notDeployed && !result && (
            <button
              onClick={handleSubmit}
              disabled={isPending || productIds.length === 0 || tooMany}
              className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
            >
              {isPending ? t('saving') : t('bulkCategorySubmit')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
